import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { getBackendOrigin, getImageUrl } from '@/utils/imagePaths';
import { getAppSocket } from '@/lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';

type CompanyInfoForm = {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  cuit?: string;
  whatsapp?: string;
  instagram?: string;
  weekdayHours?: string;
  saturdayHours?: string;
  iibb?: string;
  taxStatus?: string;
  activityStartDate?: string;
};

type CompanyArea = {
  id: string;
  name: string;
  isActive?: number;
};

type CategoryType = 'product' | 'service' | 'supply';

type Category = {
  id: string;
  name: string;
  description?: string;
  type: CategoryType;
  isActive?: number;
};

type SupplyCategory = {
  id: string;
  name: string;
};

type CategoryCreatorProps = {
  nameLabel: string;
  namePlaceholder: string;
  nameValue: string;
  onNameChange: (value: string) => void;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  descriptionValue?: string;
  onDescriptionChange?: (value: string) => void;
  typeLabel?: string;
  typeValue?: CategoryType;
  onTypeChange?: (value: CategoryType) => void;
  submitLabel: string;
  onSubmit: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  hideDescription?: boolean;
  disableTypeSelect?: boolean;
};

const CategoryCreator: React.FC<CategoryCreatorProps> = ({
  nameLabel,
  namePlaceholder,
  nameValue,
  onNameChange,
  descriptionLabel,
  descriptionPlaceholder,
  descriptionValue,
  onDescriptionChange,
  typeLabel,
  typeValue,
  onTypeChange,
  submitLabel,
  onSubmit,
  onCancel,
  disabled,
  hideDescription,
  disableTypeSelect
}) => {
  const hasDescription = Boolean(descriptionLabel) && !hideDescription;
  const hasType = Boolean(typeLabel);

  return (
    <div className="space-y-4">
      <div className={`grid gap-4 ${hasType ? 'md:grid-cols-3' : hasDescription ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
        <div>
          <Label className="text-sm text-muted-foreground">{nameLabel}</Label>
          <Input
            placeholder={namePlaceholder}
            value={nameValue}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={disabled}
          />
        </div>

        {hasDescription && (
          <div>
            <Label className="text-sm text-muted-foreground">{descriptionLabel}</Label>
            <Input
              placeholder={descriptionPlaceholder}
              value={descriptionValue}
              onChange={(e) => onDescriptionChange?.(e.target.value)}
              disabled={disabled}
            />
          </div>
        )}

        {hasType && (
          <div>
            <Label className="text-sm text-muted-foreground">{typeLabel}</Label>
            <Select
              value={typeValue}
              onValueChange={(val: CategoryType) => onTypeChange?.(val)}
              disabled={disabled || disableTypeSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="product">Producto</SelectItem>
                <SelectItem value="service">Servicio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          onClick={onSubmit}
          disabled={disabled}
          className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={disabled}>
            Cancelar
          </Button>
        )}
      </div>
    </div>
  );
};

const CompanyInfo: React.FC = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty }
  } = useForm<CompanyInfoForm>();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [logoDarkFile, setLogoDarkFile] = useState<File | null>(null);
  const [logoDarkPreview, setLogoDarkPreview] = useState<string | null>(null);

  const [areas, setAreas] = useState<CompanyArea[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areaSaving, setAreaSaving] = useState(false);
  const [areaDeletingId, setAreaDeletingId] = useState<string | null>(null);
  const [newArea, setNewArea] = useState('');
  const [newAreaIsWorkshop, setNewAreaIsWorkshop] = useState(true);

  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    type: 'product' as CategoryType
  });
  const [categorySaving, setCategorySaving] = useState(false);
  const [supplyCategoryDeletingId, setSupplyCategoryDeletingId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryType | 'all'>('all');

  const [supplyCategories, setSupplyCategories] = useState<SupplyCategory[]>([]);
  const [supplyCategoriesLoading, setSupplyCategoriesLoading] = useState(false);

  const addCacheBust = (url: string): string => {
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const [base, query = ''] = url.split('?');
    const filteredQuery = query
      .split('&')
      .filter((param) => param && !param.startsWith('ts='))
      .join('&');
    const newQuery = filteredQuery ? `${filteredQuery}&ts=${Date.now()}` : `ts=${Date.now()}`;
    return `${base}?${newQuery}`;
  };

  const resolveLogoPreview = useCallback((logoValue?: string | null): string | null => {
    if (!logoValue) return null;
    const trimmedValue = logoValue.trim();
    if (!trimmedValue) return null;
    if (trimmedValue.startsWith('blob:') || /^data:image|^https?:\/\//i.test(trimmedValue)) {
      return trimmedValue;
    }

    const normalized = trimmedValue.replace(/\\/g, '/');
    const uploadsIndex = normalized.indexOf('/uploads/');
    if (uploadsIndex >= 0) {
      const resolved = getImageUrl(normalized.slice(uploadsIndex));
      return resolved ? addCacheBust(resolved) : null;
    }

    const backendOrigin = getBackendOrigin();
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;

    if (withLeadingSlash.startsWith('/api/')) {
      return addCacheBust(`${backendOrigin}${withLeadingSlash}`);
    }

    const sanitizedValue = normalized
      .split('?')[0]
      .replace(/^\/+/, '')
      .replace(/^api\/settings\/company\/logo\//i, '')
      .replace(/^uploads\/company\//i, '')
      .trim();

    if (!sanitizedValue) return null;

    return addCacheBust(`${backendOrigin}/api/settings/company/logo/${encodeURIComponent(sanitizedValue)}`);
  }, []);

  const fetchCompany = useCallback(async () => {
    try {
      const res = await axios.get('/api/settings/company');
      const companyData = res.data || {};
      setValue('name', companyData.name || '');
      setValue('address', companyData.address || '');
      setValue('phone', companyData.phone || '');
      setValue('whatsapp', companyData.whatsapp || '');
      setValue('email', companyData.email || '');
      setValue('instagram', companyData.instagram || '');
      setValue('cuit', companyData.cuit || '');
      setValue('weekdayHours', companyData.weekdayHours || '');
      setValue('saturdayHours', companyData.saturdayHours || '');
      setValue('iibb', companyData.iibb || '');
      setValue('taxStatus', companyData.taxStatus || '');
      setValue('activityStartDate', companyData.activityStartDate || '');
      const previewUrl = resolveLogoPreview(companyData.logo);
      setLogoPreview(previewUrl);
      const darkPreviewUrl = resolveLogoPreview(companyData.logoDark);
      setLogoDarkPreview(darkPreviewUrl);
    } catch (e) {
      // Sin config previa se dejan valores en blanco
    }
  }, [resolveLogoPreview, setValue]);

  const fetchAreas = useCallback(async () => {
    try {
      setAreasLoading(true);
      const res = await axios.get('/api/settings/areas');
      setAreas(res.data?.areas || []);
    } catch (error: any) {
      console.error('Error fetching areas', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar las areas');
    } finally {
      setAreasLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const res = await axios.get('/api/categories');
      setCategories(res.data?.categories || []);
    } catch (error: any) {
      console.error('Error fetching categories', error);
      toast.error('No se pudieron cargar las categorias');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchSupplyCategories = useCallback(async () => {
    try {
      setSupplyCategoriesLoading(true);
      const res = await axios.get('/api/settings/supply-categories');
      setSupplyCategories(res.data?.categories || []);
    } catch (error: any) {
      console.error('Error fetching supply categories', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar las categorias de insumos');
    } finally {
      setSupplyCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompany();
    fetchAreas();
    fetchCategories();
    fetchSupplyCategories();
  }, [fetchCompany, fetchAreas, fetchCategories, fetchSupplyCategories]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  const notifiedRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/settings/company') ||
          path.startsWith('/api/settings/areas') ||
          path.startsWith('/api/categories') ||
          path.startsWith('/api/settings/supply-categories')
        )
      ) {
        return;
      }

      const editing =
        isDirty ||
        Boolean(logoFile) ||
        Boolean(logoDarkFile) ||
        Boolean(editingCategory) ||
        areaSaving ||
        categorySaving;

      if (editing) {
        pendingRefreshRef.current = true;
        if (!notifiedRef.current && isDirty) {
          notifiedRef.current = true;
          toast.message('Se detectaron cambios externos. Se actualizará al guardar o descartar cambios.');
        }
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchCompany();
        fetchAreas();
        fetchCategories();
        fetchSupplyCategories();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchCompany, fetchAreas, fetchCategories, fetchSupplyCategories, isDirty, logoFile, logoDarkFile, editingCategory, areaSaving, categorySaving]);

  useEffect(() => {
    const editing =
      isDirty ||
      Boolean(logoFile) ||
      Boolean(editingCategory) ||
      areaSaving ||
      categorySaving;

    if (editing) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    notifiedRef.current = false;
    fetchCompany();
    fetchAreas();
    fetchCategories();
    fetchSupplyCategories();
  }, [isDirty, logoFile, logoDarkFile, editingCategory, areaSaving, categorySaving, fetchCompany, fetchAreas, fetchCategories, fetchSupplyCategories]);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  useEffect(() => {
    return () => {
      if (logoDarkPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(logoDarkPreview);
      }
    };
  }, [logoDarkPreview]);

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (logoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
    } else {
      setLogoPreview(null);
    }
  };

  const onLogoDarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (logoDarkPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(logoDarkPreview);
    }
    setLogoDarkFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoDarkPreview(url);
    } else {
      setLogoDarkPreview(null);
    }
  };

  const onSubmit = async (data: CompanyInfoForm) => {
    try {
      await axios.post('/api/settings/company', data);
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        await axios.post('/api/settings/company/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        window.dispatchEvent(new CustomEvent('company-logo-updated'));
      }
      if (logoDarkFile) {
        const formData = new FormData();
        formData.append('logo', logoDarkFile);
        await axios.post('/api/settings/company/logo-dark', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        window.dispatchEvent(new CustomEvent('company-logo-dark-updated'));
      }
      toast.success('Informacion de la empresa guardada');
      setLogoFile(null);
      setLogoDarkFile(null);
      await fetchCompany();
    } catch (error: any) {
      console.error('Error guardando informacion de empresa:', error);
      toast.error(error.response?.data?.error || 'Error guardando la informacion');
    }
  };

  const createArea = async () => {
    if (!newArea.trim()) {
      toast.error('Ingresa un nombre de area');
      return;
    }
    try {
      setAreaSaving(true);
      await axios.post('/api/settings/areas', { name: newArea.trim(), isWorkshop: newAreaIsWorkshop });
      toast.success('Area creada');
      setNewArea('');
      setNewAreaIsWorkshop(true);
      fetchAreas();
    } catch (error: any) {
      console.error('Error creating area', error);
      toast.error(error.response?.data?.error || 'No se pudo crear el area');
    } finally {
      setAreaSaving(false);
    }
  };

  const deleteArea = async (id: string) => {
    if (!await confirmDialog('Eliminar esta area?')) return;
    try {
      setAreaDeletingId(id);
      await axios.delete(`/api/settings/areas/${id}`);
      toast.success('Area eliminada');
      fetchAreas();
    } catch (error: any) {
      console.error('Error deleting area', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar el area');
    } finally {
      setAreaDeletingId(null);
    }
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    const isSupply = categoryForm.type === 'supply';

    if (isSupply) {
      try {
        setCategorySaving(true);
        if (editingCategory?.type === 'supply') {
          await axios.put(`/api/settings/supply-categories/${editingCategory.id}`, { name: categoryForm.name.trim() });
          toast.success('Categoria de insumo actualizada');
        } else {
          await axios.post('/api/settings/supply-categories', { name: categoryForm.name.trim() });
          toast.success('Categoria de insumo creada');
        }
        setCategoryForm({ name: '', description: '', type: 'product' });
        setEditingCategory(null);
        fetchSupplyCategories();
      } catch (error: any) {
        console.error('Error saving supply category', error);
        toast.error(error.response?.data?.error || 'No se pudo guardar la categoria de insumo');
      } finally {
        setCategorySaving(false);
      }
      return;
    }

    try {
      setCategorySaving(true);
      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory.id}`, categoryForm);
        toast.success('Categoria actualizada');
      } else {
        await axios.post('/api/categories', categoryForm);
        toast.success('Categoria creada');
      }
      setCategoryForm({ name: '', description: '', type: 'product' });
      setEditingCategory(null);
      fetchCategories();
    } catch (error: any) {
      console.error('Error saving category', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar la categoria');
    } finally {
      setCategorySaving(false);
    }
  };

  const deleteCategory = async (id: string) => {
    if (!await confirmDialog('Eliminar esta categoria?')) return;
    try {
      await axios.delete(`/api/categories/${id}`);
      toast.success('Categoria eliminada');
      fetchCategories();
    } catch (error: any) {
      console.error('Error deleting category', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar la categoria');
    }
  };

  const deleteSupplyCategory = async (id: string) => {
    if (!await confirmDialog('Eliminar esta categoria de insumo?')) return;
    try {
      setSupplyCategoryDeletingId(id);
      await axios.delete(`/api/settings/supply-categories/${id}`);
      toast.success('Categoria de insumo eliminada');
      fetchSupplyCategories();
    } catch (error: any) {
      console.error('Error deleting supply category', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar la categoria de insumo');
    } finally {
      setSupplyCategoryDeletingId(null);
    }
  };

  const mappedSupplyCategories = useMemo(
    () =>
      supplyCategories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        description: '',
        type: 'supply' as CategoryType
      })),
    [supplyCategories]
  );

  const allCategories = useMemo(() => [...categories, ...mappedSupplyCategories], [categories, mappedSupplyCategories]);

  const filteredCategories = useMemo(() => {
    if (categoryFilter === 'all') return allCategories;
    return allCategories.filter((cat) => cat.type === categoryFilter);
  }, [allCategories, categoryFilter]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">Informacion de la empresa</h1>
          <p className="text-sm text-muted-foreground">Datos generales, logo y areas internas.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Datos de contacto</CardTitle>
            <CardDescription>Se usan en cabeceras, remitos, recordatorios y en la web de contacto.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label className="text-sm text-muted-foreground">Nombre de la empresa *</Label>
                  <Input {...register('name', { required: 'Nombre requerido' })} placeholder="Mi Empresa SRL" />
                  {errors?.name && <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>}
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Telefono</Label>
                  <Input {...register('phone')} placeholder="3416858698" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">WhatsApp</Label>
                  <Input {...register('whatsapp')} placeholder="+54 9 ..." />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Email de contacto web</Label>
                  <Input type="email" {...register('email')} placeholder="hola@miempresa.com" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Instagram</Label>
                  <Input {...register('instagram')} placeholder="@miempresa" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm text-muted-foreground">Direccion</Label>
                  <Input {...register('address')} placeholder="Calle 123, Rosario" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">CUIT</Label>
                  <Input {...register('cuit')} placeholder="20xxxxxxxxx" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Ingresos Brutos</Label>
                  <Input {...register('iibb')} placeholder="091-728559-8" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Condicion frente al IVA</Label>
                  <Input {...register('taxStatus')} placeholder="IVA Responsable Inscripto" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Fecha Inicio de Actividades</Label>
                  <Input {...register('activityStartDate')} placeholder="29/10/2019" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Horario Lun a Vie</Label>
                  <Input {...register('weekdayHours')} placeholder="8:00 a 18:00" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Horario Sabado</Label>
                  <Input {...register('saturdayHours')} placeholder="9:00 a 13:00" />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
                  <p className="text-sm font-semibold text-muted-foreground">Logo visible en PDFs</p>
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card/70 p-4 text-center">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Vista previa logo" className="h-24 max-w-full object-contain" />
                    ) : (
                      <div className="flex h-24 w-32 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
                        Sin logo
                      </div>
                    )}
                    <div className="w-full space-y-2">
                      <input
                        id="company-logo"
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={onLogoChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="company-logo"
                        className="inline-flex w-full cursor-pointer items-center justify-center rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground shadow-sm transition hover:bg-accent hover:text-accent-foreground"
                      >
                        Seleccionar archivo
                      </label>
                      {logoPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full text-sm text-destructive hover:text-destructive"
                          onClick={() => {
                            if (logoPreview?.startsWith('blob:')) {
                              URL.revokeObjectURL(logoPreview);
                            }
                            setLogoPreview(null);
                            setLogoFile(null);
                          }}
                        >
                          Quitar logo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Logo modo oscuro</p>
                    <p className="text-xs text-muted-foreground">Se usa cuando el sistema esta en modo oscuro</p>
                  </div>
                  <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card/70 p-4 text-center">
                    {logoDarkPreview ? (
                      <img src={logoDarkPreview} alt="Vista previa logo oscuro" className="h-24 max-w-full object-contain" />
                    ) : (
                      <div className="flex h-24 w-32 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
                        Sin logo oscuro
                      </div>
                    )}
                    <div className="w-full space-y-2">
                      <input
                        id="company-logo-dark"
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={onLogoDarkChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="company-logo-dark"
                        className="inline-flex w-full cursor-pointer items-center justify-center rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground shadow-sm transition hover:bg-accent hover:text-accent-foreground"
                      >
                        Seleccionar archivo
                      </label>
                      {logoDarkPreview && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full text-sm text-destructive hover:text-destructive"
                          onClick={() => {
                            if (logoDarkPreview?.startsWith('blob:')) {
                              URL.revokeObjectURL(logoDarkPreview);
                            }
                            setLogoDarkPreview(null);
                            setLogoDarkFile(null);
                          }}
                        >
                          Quitar logo oscuro
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-card/60 p-4 shadow-sm space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Areas de la empresa</p>
                    <p className="text-sm text-muted-foreground">Usadas en usuarios y pedidos internos.</p>
                  </div>

                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <Input
                      placeholder="Nombre del area"
                      value={newArea}
                      onChange={(e) => setNewArea(e.target.value)}
                      disabled={areaSaving}
                      className="md:w-64"
                    />
                    <Button onClick={createArea} disabled={areaSaving}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {areaSaving ? 'Guardando...' : 'Crear area'}
                    </Button>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={newAreaIsWorkshop}
                      onChange={(e) => setNewAreaIsWorkshop(e.target.checked)}
                    />
                    <span>Es área del taller (crea su estación y aparece en servicios). Desmarcá si es solo administrativa.</span>
                  </label>

                  <div className="space-y-2">
                    {areasLoading ? (
                      <p className="text-sm text-muted-foreground">Cargando areas...</p>
                    ) : areas.length ? (
                      areas.map((area) => (
                        <div
                          key={area.id}
                          className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                        >
                          <span className="text-sm">{area.name}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteArea(area.id)}
                            disabled={areaDeletingId === area.id}
                          >
                            <TrashIcon className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No hay areas configuradas.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-white/90"
                >
                  Guardar cambios
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Categorías de productos y servicios</CardTitle>
            <CardDescription>Los insumos se clasifican por área (no usan estas categorías).</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              <div className="space-y-4">
                <CategoryCreator
                  nameLabel="Nombre *"
                  namePlaceholder="Nombre de categoria"
                  nameValue={categoryForm.name}
                  onNameChange={(value) => setCategoryForm({ ...categoryForm, name: value })}
                  descriptionLabel="Descripcion"
                  descriptionPlaceholder="Descripcion opcional"
                  descriptionValue={categoryForm.description}
                  onDescriptionChange={(value) => setCategoryForm({ ...categoryForm, description: value })}
                  typeLabel="Tipo *"
                  typeValue={categoryForm.type}
                  onTypeChange={(val) => setCategoryForm({ ...categoryForm, type: val })}
                  submitLabel={`${editingCategory ? 'Actualizar' : 'Crear'} categoria`}
                  onSubmit={saveCategory}
                  onCancel={
                    editingCategory
                      ? () => {
                          setEditingCategory(null);
                          setCategoryForm({ name: '', description: '', type: 'product' });
                        }
                      : undefined
                  }
                  hideDescription={categoryForm.type === 'supply'}
                  disabled={categorySaving}
                  disableTypeSelect={!!editingCategory}
                />

                <div className="flex flex-wrap gap-2">
                  {(['all', 'product', 'service'] as (CategoryType | 'all')[]).map((filter) => (
                    <Button
                      key={filter}
                      variant={categoryFilter === filter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCategoryFilter(filter)}
                      className="rounded-full px-3"
                    >
                      {filter === 'all'
                        ? 'Todas'
                        : filter === 'product'
                          ? 'Productos'
                          : filter === 'service'
                            ? 'Servicios'
                            : 'Insumos'}
                    </Button>
                  ))}
                </div>

                <div className="space-y-2">
                  {categoriesLoading || supplyCategoriesLoading ? (
                    <p className="text-sm text-muted-foreground">Cargando categorias...</p>
                  ) : filteredCategories.length ? (
                    <div className="grid gap-2 md:grid-cols-2">
                      {filteredCategories.map((cat) => (
                        <div
                          key={cat.id}
                          className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{cat.name}</span>
                              <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
                                {cat.type === 'product'
                                  ? 'Producto'
                                  : cat.type === 'service'
                                    ? 'Servicio'
                                    : 'Insumo'}
                              </span>
                            </div>
                            {cat.description && cat.type !== 'supply' && (
                              <p className="text-xs text-muted-foreground mt-1">{cat.description}</p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingCategory({ ...cat, isActive: 1 });
                                setCategoryForm({
                                  name: cat.name,
                                  description: cat.description || '',
                                  type: cat.type
                                });
                              }}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                cat.type === 'supply' ? deleteSupplyCategory(cat.id) : deleteCategory(cat.id)
                              }
                              disabled={cat.type === 'supply' ? supplyCategoryDeletingId === cat.id : false}
                            >
                              <TrashIcon className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No hay categorias configuradas.</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompanyInfo;
